import { useEffect } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/query-client'
import { Layout } from '@/components/layout'
import { ProtectedRoute, AdminRoute } from '@/components/auth'
import { ToastProvider } from '@/components/common/Toast'
import { ErrorBoundary } from '@/components/common'
import { useAuthStore } from '@/store/auth.store'
import { authApi } from '@/api'
import { decodeJwtExpMs } from '@/lib/jwt'

import { Home } from '@/pages/Home'
import { Login, Signup, PasswordRecovery, ResetPassword } from '@/pages/auth'
import { PostDetailPage } from '@/pages/PostDetailPage'
import { NewPostPage } from '@/pages/NewPostPage'
import { EditPostPage } from '@/pages/EditPostPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { FollowersPage } from '@/pages/FollowersPage'
import { FollowingPage } from '@/pages/FollowingPage'
import { MessagesPage } from '@/pages/MessagesPage'
import { ThreadPage } from '@/pages/ThreadPage'
import { NotificationsPage } from '@/pages/NotificationsPage'
import { ClapsReceivedPage } from '@/pages/ClapsReceivedPage'
import { SearchPage } from '@/pages/SearchPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { EventsPage } from '@/pages/EventsPage'
import { BazaarPage } from '@/pages/BazaarPage'
import { AdminDashboardPage } from '@/pages/AdminDashboardPage'
import { AdminReportsPage } from '@/pages/AdminReportsPage'
import { AdminCategoriesPage } from '@/pages/AdminCategoriesPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { EventForm } from '@/pages/posts/EventForm'
import { BazaarForm } from '@/pages/posts/BazaarForm'
import { ActionForm } from '@/pages/posts/ActionForm'
import { About } from '@/pages/static/About'
import { Conduct } from '@/pages/static/Conduct'
import { Privacy } from '@/pages/static/Privacy'
import { HowToInstallPage } from '@/pages/HowToInstallPage'
import { GroupPage } from '@/pages/GroupPage'
import { GroupSettingsPage } from '@/pages/GroupSettingsPage'
import { MyGroupsPage } from '@/pages/MyGroupsPage'
import { CreateGroupPage } from '@/pages/CreateGroupPage'

function SilentRefresh() {
  const { accessToken, setAuth, clearAuth, setRestored } = useAuthStore()

  useEffect(() => {
    authApi
      .refresh()
      .then((data) => setAuth(data.accessToken, data.account))
      .catch(() => { clearAuth(); setRestored() })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Proactive refresh before the access token expires (15 min by default —
  // see JWT_ACCESS_EXPIRES), so background polls (unread counts, etc.)
  // never fire with a stale token. Without this, apiClient's interceptor
  // only refreshes reactively AFTER a request 401s — that 401 still shows
  // up as a real network error in devtools even though the app recovers
  // from it, which is the noise this avoids rather than "fixes" per se.
  useEffect(() => {
    if (!accessToken) return
    const expMs = decodeJwtExpMs(accessToken)
    if (!expMs) return
    const REFRESH_BUFFER_MS = 60_000
    const delay = Math.max(expMs - Date.now() - REFRESH_BUFFER_MS, 0)
    const timer = setTimeout(() => {
      authApi.refresh().then((data) => setAuth(data.accessToken, data.account)).catch(() => clearAuth())
    }, delay)
    return () => clearTimeout(timer)
  }, [accessToken, setAuth, clearAuth])

  return null
}

function AuthCacheSync() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  useEffect(() => {
    if (isAuthenticated) {
      void queryClient.invalidateQueries()
    }
  }, [isAuthenticated])

  return null
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <SilentRefresh />
            <AuthCacheSync />
            <Routes>
              {/* Auth screens — no shell layout */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/password-recovery" element={<PasswordRecovery />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Shell layout wraps all other routes */}
              <Route element={<Layout />}>
                <Route index element={<ErrorBoundary><Home /></ErrorBoundary>} />
                <Route path="search" element={<SearchPage />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="bazaar" element={<BazaarPage />} />
                <Route path="about" element={<About />} />
                <Route path="conduct" element={<Conduct />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="how-to-install" element={<HowToInstallPage />} />
                <Route path="posts/:id" element={<PostDetailPage />} />

                <Route
                  path="post/new"
                  element={
                    <ProtectedRoute>
                      <NewPostPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="post/:id/edit"
                  element={
                    <ProtectedRoute>
                      <EditPostPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="posts/new/event"
                  element={
                    <ProtectedRoute>
                      <EventForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="posts/new/bazaar"
                  element={
                    <ProtectedRoute>
                      <BazaarForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="posts/new/action"
                  element={
                    <ProtectedRoute>
                      <ActionForm />
                    </ProtectedRoute>
                  }
                />

                {/* Temporary entry points (Phase B1, pre-nav-refactor) */}
                <Route
                  path="groups"
                  element={
                    <ProtectedRoute>
                      <MyGroupsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="groups/create"
                  element={
                    <ProtectedRoute>
                      <CreateGroupPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="groups/:slug" element={<GroupPage />} />
                <Route
                  path="groups/:slug/settings"
                  element={
                    <ProtectedRoute>
                      <GroupSettingsPage />
                    </ProtectedRoute>
                  }
                />

                <Route path=":handle" element={<ProfilePage />} />
                <Route path=":handle/followers" element={<FollowersPage />} />
                <Route path=":handle/following" element={<FollowingPage />} />

                <Route
                  path="messages"
                  element={
                    <ProtectedRoute>
                      <MessagesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="messages/:id"
                  element={
                    <ProtectedRoute>
                      <ThreadPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="notifications"
                  element={
                    <ProtectedRoute>
                      <NotificationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="claps"
                  element={
                    <ProtectedRoute>
                      <ClapsReceivedPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/profile"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="admin"
                  element={
                    <AdminRoute>
                      <AdminDashboardPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/reports"
                  element={
                    <AdminRoute>
                      <AdminReportsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/categories"
                  element={
                    <AdminRoute capability="manage_categories">
                      <AdminCategoriesPage />
                    </AdminRoute>
                  }
                />

                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
